import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import {
  useGetOTP,
  useResetPassword,
  useVerifyEmail,
} from "@/hooks/use-mutations";
import { useState } from "react";
import Throbber from "@/components/throbber";
import Logo from "@/components/logo";
import { handleError } from "@/lib/helper";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";

const schema = z
  .object({
    email: z.string().email(),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number")
      .regex(/[\W_]/, "Password must contain at least one special character"),
    confirmPassword: z.string().min(8),
    otp: z.string().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

const ResetPassword = () => {
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      otp: "",
      email: "",
    },
    mode: "onTouched",
  });
  const { mutateAsync: getOtp, isPending: isGettingOtp } = useGetOTP();
  const { mutateAsync: verify, isPending: isVerifying } = useVerifyEmail();
  const { mutateAsync: resetPassword, isPending: isResetting } =
    useResetPassword({
      onSuccess: () => {
        navigate("/login");
      },
    });

  const handleGetOtp = async (data: { email: string }) => {
    const { email: emailAddress } = data;
    try {
      await getOtp({ emailAddress, purpose: "forget-password" });
      setStep(3);
    } catch (error) {
      handleError(error);
    }
  };

  const onSubmit = (data: {
    email: string;
    password: string;
    confirmPassword: string;
    otp?: string;
  }) => {
    const { confirmPassword, ...rest } = data;
    resetPassword({
      ...rest,
      newPassword: data.password,
      otp: parseInt(data.otp || "0"),
    });
  };

  return (
    <div className="text-auth-text relative h-full w-full overflow-hidden">
      <div className="relative flex min-h-dvh">
        {/* Left panel. `aspect-[617/832]` matches the artwork's viewBox so the
            column is the correct width before the image decodes, and
            `bg-auth-panel` fills it in the meantime. */}
        <div className="bg-auth-panel fixed inset-y-0 left-0 hidden aspect-617/832 h-screen lg:block">
          <img
            src="/images/auth-image2.svg"
            className="h-full w-auto"
            alt="The New Standard of Digital Finance. Easily create virtual cards to handle every online purchase, bill, and subscription."
          />
        </div>
        {/* The layer above is `fixed` and out of flow, so this reserves the
            column width. Same ratio, no second image download. */}
        <div className="hidden aspect-617/832 h-screen lg:block" />
        <div className="bg-auth-surface relative flex grow flex-col items-center justify-center py-10">
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="relative flex w-[320px] flex-col items-start md:w-[400px] lg:w-full lg:max-w-sm"
          >
            <Logo className="text-auth-action w-32" />

            <Form {...form}>
              <div className="mt-6 flex w-full flex-col gap-2.5 lg:min-w-sm">
                {step === 1 && (
                  <>
                    <Button
                      className="absolute -top-32 -left-2 size-8"
                      variant={"ghost"}
                      type="button"
                      onClick={() => navigate("/login")}
                    >
                      <ArrowLeft />
                    </Button>
                    <div>
                      <h1 className="text-2xl font-semibold">
                        Forgot Password
                      </h1>
                      <p>Enter email below to reset password.</p>
                    </div>

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem className="mt-4 flex flex-col gap-1">
                          <FormLabel className="text-xs">
                            Email address
                          </FormLabel>
                          <FormControl>
                            <Input
                              className="border-auth-stroke placeholder:text-auth-muted rounded placeholder:text-[10px]"
                              {...field}
                              type="email"
                              onChange={(e) => {
                                field.onChange(e);
                                form.clearErrors("email");
                              }}
                              onBlur={(e) => {
                                const cleaned = e.target.value
                                  .replace(/\s+/g, "")
                                  .toLowerCase();
                                form.setValue("email", cleaned, {
                                  shouldValidate: true,
                                });
                                field.onBlur();
                              }}
                              placeholder="Enter your email address"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      onClick={async () => {
                        const isValid = await form.trigger(["email"]);
                        if (!isValid) return;
                        const verifyResponse = await verify({
                          email: form.getValues("email") ?? "",
                        });
                        if (!verifyResponse.data.emailExists) {
                          form.setError("email", {
                            type: "required",
                            message: "Email does not exist",
                          });
                          return;
                        }
                        setStep(2);
                      }}
                      className="bg-auth-action text-auth-on-action hover:bg-auth-action/90 mt-6 h-11 w-full rounded"
                      type="button"
                    >
                      {isVerifying ? <Throbber /> : "Reset"}
                    </Button>
                  </>
                )}
                {step === 2 && (
                  <>
                    <Button
                      className="absolute -top-32 -left-2 size-8"
                      variant={"ghost"}
                      type="button"
                      onClick={() => setStep(1)}
                    >
                      <ArrowLeft />
                    </Button>
                    <div>
                      <h1 className="text-2xl font-semibold">
                        Set new password
                      </h1>
                      <p>Kindly enter your new password</p>
                    </div>

                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-1">
                          <FormLabel className="text-xs">Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                className="border-auth-stroke placeholder:text-auth-muted rounded placeholder:text-[10px]"
                                type={showPassword ? "text" : "password"}
                                {...field}
                                placeholder="Enter your password"
                              />
                              <Button
                                type="button"
                                variant={"ghost"}
                                className="text-auth-muted absolute right-0"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? <Eye /> : <EyeOff />}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-1">
                          <FormLabel className="text-xs">
                            Confirm password
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                className="border-auth-stroke placeholder:text-auth-muted rounded placeholder:text-[10px]"
                                type={showConfirmPassword ? "text" : "password"}
                                {...field}
                                placeholder="Confirm your password"
                              />
                              <Button
                                type="button"
                                variant={"ghost"}
                                className="text-auth-muted absolute right-0"
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                              >
                                {showConfirmPassword ? <Eye /> : <EyeOff />}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      disabled={isGettingOtp}
                      className="bg-auth-action text-auth-on-action hover:bg-auth-action/90 mt-6 h-11 w-full rounded"
                      type="button"
                      onClick={() => handleGetOtp(form.getValues())}
                    >
                      {isGettingOtp ? <Throbber /> : "Continue"}
                    </Button>
                  </>
                )}

                {/* Step 2 */}
                {step === 3 && (
                  <>
                    <Button
                      className="absolute -top-32 -left-2 size-8"
                      variant={"ghost"}
                      type="button"
                      onClick={() => setStep(2)}
                    >
                      <ArrowLeft />
                    </Button>
                    <div>
                      <h1 className="text-2xl font-semibold">Verification</h1>
                      <p className="text-auth-muted text-sm leading-3.5">
                        We’ve sent a 6-digit code to your email, enter the code
                        below to verify
                      </p>
                    </div>

                    <div className="mt-2 flex w-full flex-col gap-2.5">
                      <FormField
                        control={form.control}
                        name="otp"
                        render={({ field }) => (
                          <FormItem className="flex flex-col gap-1">
                            <FormLabel className="mb-1 text-xs font-normal">
                              Enter verification code
                            </FormLabel>
                            <FormControl>
                              <InputOTP
                                autoFocus
                                autoComplete="off"
                                pattern={REGEXP_ONLY_DIGITS}
                                maxLength={6}
                                {...field}
                              >
                                <InputOTPGroup className="*:border-auth-otp-stroke gap-3 *:!rounded *:border *:shadow-none">
                                  <InputOTPSlot index={0} />
                                  <InputOTPSlot index={1} />
                                  <InputOTPSlot index={2} />
                                  <InputOTPSlot index={3} />
                                  <InputOTPSlot index={4} />
                                  <InputOTPSlot index={5} />
                                </InputOTPGroup>
                              </InputOTP>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button
                        disabled={isResetting}
                        className="bg-auth-action text-auth-on-action hover:bg-auth-action/90 mt-4 h-11 w-full rounded"
                        type="submit"
                      >
                        {isResetting ? <Throbber /> : "Reset"}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </Form>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
